import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ConsentGate({ onAccepted }) {
  const { acceptConsent, apiError } = useAuth();
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);

  const accept = async () => {
    setLoading(true);
    try {
      await acceptConsent();
      onAccepted?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto glass-card rounded-2xl p-8" data-testid="consent-gate">
      <span className="grid place-items-center h-12 w-12 rounded-full bg-primary/15 text-primary mb-5">
        <ShieldCheck strokeWidth={1.5} />
      </span>
      <h2 className="font-serif text-2xl mb-3">Before your first scan</h2>
      <div className="text-sm text-muted-foreground space-y-3 leading-relaxed mb-6">
        <p>VoiceMatrix AI analyses the frequency patterns in your voice to surface wellness <strong className="text-secondary-foreground">risk indicators</strong> — areas worth observing.</p>
        <p>This is <strong className="text-secondary-foreground">not a medical diagnosis</strong> and does not predict, confirm, or rule out any disease. Always confirm any concern with a qualified physician.</p>
        <p>Your voice recording is processed to generate your report. By continuing you consent to this analysis.</p>
      </div>
      <label className="flex items-start gap-3 mb-6 cursor-pointer">
        <Checkbox checked={checked} onCheckedChange={(v) => setChecked(!!v)} data-testid="consent-checkbox" className="mt-0.5" />
        <span className="text-sm text-secondary-foreground">I understand this is a wellness screening, not a diagnosis, and I consent to my voice being analysed.</span>
      </label>
      <Button onClick={accept} disabled={!checked || loading} className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/85 font-semibold" data-testid="consent-accept-btn">
        {loading && <Loader2 size={16} className="animate-spin" />} I agree — continue
      </Button>
    </div>
  );
}
